/** @jsxImportSource @emotion/react */
/**
 * PendingBusinessSetupBanner Component
 * SESSION_338: Portal access for pending business claims
 *
 * Displays a distinctive banner for users whose new business claim
 * is being processed. Shown on the dashboard while admins set up
 * the business profile.
 *
 * Features:
 * - Teal/cyan gradient distinct from other banners
 * - Animated progress indicator
 * - Clear messaging about setup status
 * - Expected timeframe notification
 */

import React from 'react';
import { Box, Group, Stack } from '@mantine/core';
import {
  IconLoader2,
  IconClock,
  IconBuildingStore,
  IconChartBar,
  IconBell
} from '@tabler/icons-react';
import type { PendingBusinessSetupBannerProps } from './PendingBusinessSetupBanner.types';
import {
  bannerContainerStyles,
  setupBannerStyles,
  contentWrapperStyles,
  geometricAccentStyles,
  geometricAccentSecondaryStyles,
  geometricAccentTertiaryStyles,
  iconContainerStyles,
  setupIconStyles,
  titleStyles,
  messageStyles,
  businessNameStyles,
  progressBarContainerStyles,
  progressBarShimmerStyles,
  timeframeStyles,
  featureBadgesStyles,
  featureBadgeStyles
} from './PendingBusinessSetupBanner.styles';

/**
 * PendingBusinessSetupBanner - Displays business setup progress to users
 */
export const PendingBusinessSetupBanner: React.FC<PendingBusinessSetupBannerProps> = ({
  businessName
}): React.ReactElement => {
  return (
    <Box
      css={[bannerContainerStyles, setupBannerStyles]}
      role="status"
      aria-live="polite"
    >
      {/* Geometric accent shapes */}
      <Box css={geometricAccentStyles} aria-hidden="true" />
      <Box css={geometricAccentSecondaryStyles} aria-hidden="true" />
      <Box css={geometricAccentTertiaryStyles} aria-hidden="true" />

      <Box css={contentWrapperStyles}>
        <Group gap="md" align="flex-start" wrap="nowrap">
          {/* Icon */}
          <Box css={iconContainerStyles}>
            <Box css={setupIconStyles}>
              <IconLoader2 size={24} color="#ffffff" />
            </Box>
          </Box>

          {/* Content */}
          <Stack gap={8} style={{ flex: 1 }}>
            <h3 css={titleStyles}>Business Setup In Progress</h3>
            <p css={messageStyles}>
              Our team is setting up{' '}
              <span css={businessNameStyles}>{businessName}</span>{' '}
              so we can start monitoring social media for mentions of your business.
            </p>

            {/* What you'll get section */}
            <Box css={featureBadgesStyles}>
              <span css={featureBadgeStyles}>
                <IconBuildingStore size={12} />
                Business Profile
              </span>
              <span css={featureBadgeStyles}>
                <IconChartBar size={12} />
                Mention Analytics
              </span>
              <span css={featureBadgeStyles}>
                <IconBell size={12} />
                Email Alerts
              </span>
            </Box>

            {/* Animated progress indicator */}
            <Box css={progressBarContainerStyles}>
              <Box css={progressBarShimmerStyles} />
            </Box>

            {/* Expected timeframe */}
            <p css={timeframeStyles}>
              <IconClock size={14} />
              Typically less than 2 business days. You&apos;ll receive an email when ready.
            </p>
          </Stack>
        </Group>
      </Box>
    </Box>
  );
};
